import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Company, Subscription, AuthState, LoginCredentials, RegisterData } from '../types';
import { 
  initializeStorage, 
  getCurrentUser, 
  setCurrentUser, 
  authenticateUser, 
  logoutUser,
  getCompanyById,
  getSubscriptionByCompanyId,
  createUser,
  createCompany,
  createSubscription,
  createInvoice,
  getOnboardingData,
  setOnboardingData,
  clearOnboardingData,
  getUserByEmail
} from '../services/storage';
import { v4 as uuidv4 } from '../lib/uuid';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  selectPlan: (planId: string, billingCycle: 'monthly' | 'yearly') => void;
  completePayment: (paymentData: any) => Promise<{ success: boolean; error?: string }>;
  updateProfile: (data: Partial<User>) => Promise<boolean>;
  isAdmin: boolean;
  refreshUserData: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    company: null,
    subscription: null,
    isAuthenticated: false,
    isLoading: true
  });

  useEffect(() => {
    initializeStorage();
    const user = getCurrentUser();
    if (user) {
      loadUserData(user);
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const loadUserData = (user: User) => {
    const company = user.companyId ? getCompanyById(user.companyId) ?? null : null;
    const subscription = company ? getSubscriptionByCompanyId(company.id) ?? null : null;
    
    setState({
      user,
      company,
      subscription,
      isAuthenticated: true,
      isLoading: false
    });
  };

  const refreshUserData = () => {
    if (state.user) {
      loadUserData(state.user);
    }
  };

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    const user = authenticateUser(credentials.email, credentials.password);
    if (user) {
      loadUserData(user);
      return true;
    }
    return false;
  };

  const logout = () => {
    logoutUser();
    setState({
      user: null,
      company: null,
      subscription: null,
      isAuthenticated: false,
      isLoading: false
    });
  };

  const register = async (data: RegisterData): Promise<{ success: boolean; error?: string }> => {
    // Check if email already exists
    if (getUserByEmail(data.adminEmail)) {
      return { success: false, error: 'Este email já está em uso' };
    }

    // Check if CNPJ already exists
    // (Would need getCompanyByCnpj function in storage)

    // Create onboarding data
    setOnboardingData({
      companyData: {
        name: data.companyName,
        cnpj: data.cnpj,
        email: data.email,
        phone: data.phone
      },
      adminData: {
        name: data.adminName,
        email: data.adminEmail,
        password: data.password
      },
      step: 'plan'
    });

    return { success: true };
  };

  const selectPlan = (planId: string, billingCycle: 'monthly' | 'yearly') => {
    const onboardingData = getOnboardingData() || {};
    setOnboardingData({
      ...onboardingData,
      planId,
      billingCycle,
      step: 'payment'
    });
  };

  const completePayment = async (_paymentData: any): Promise<{ success: boolean; error?: string }> => {
    try {
      const onboardingData = getOnboardingData();
      if (!onboardingData || !onboardingData.planId) {
        return { success: false, error: 'Dados de onboarding não encontrados' };
      }

      const { companyData, adminData, planId, billingCycle } = onboardingData;

      // Create company
      const companyId = uuidv4();
      const now = new Date();
      const oneYearLater = new Date(now);
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);

      const company: Company = {
        id: companyId,
        name: companyData.name,
        cnpj: companyData.cnpj,
        email: companyData.email,
        phone: companyData.phone,
        address: '',
        city: '',
        state: '',
        zipCode: '',
        planId: planId,
        status: 'active',
        createdAt: now.toISOString().split('T')[0],
        expiresAt: oneYearLater.toISOString().split('T')[0]
      };
      createCompany(company);

      // Create admin user
      const user: User = {
        id: uuidv4(),
        name: adminData.name,
        email: adminData.email,
        role: 'client',
        companyId: companyId,
        status: 'active',
        createdAt: now.toISOString().split('T')[0]
      };
      createUser(user);

      // Get plan details
      const { getPlanById } = await import('../services/storage');
      const plan = getPlanById(planId);
      if (!plan) {
        return { success: false, error: 'Plano não encontrado' };
      }

      const amount = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;

      // Create subscription
      const subscription: Subscription = {
        id: uuidv4(),
        companyId: companyId,
        planId: planId,
        billingCycle: billingCycle,
        status: 'active',
        startDate: now.toISOString().split('T')[0],
        endDate: oneYearLater.toISOString().split('T')[0],
        nextBillingDate: billingCycle === 'monthly' 
          ? new Date(now.setMonth(now.getMonth() + 1)).toISOString().split('T')[0]
          : oneYearLater.toISOString().split('T')[0],
        amount: amount
      };
      createSubscription(subscription);

      // Create initial invoice
      const invoice = {
        id: uuidv4(),
        companyId: companyId,
        subscriptionId: subscription.id,
        amount: amount,
        status: 'paid' as const,
        dueDate: now.toISOString().split('T')[0],
        paidDate: now.toISOString().split('T')[0],
        invoiceNumber: `INV-${new Date().getFullYear()}-${String(getInvoices().length + 1).padStart(4, '0')}`,
        description: `Assinatura ${plan.name} - ${billingCycle === 'monthly' ? 'Mensal' : 'Anual'}`
      };
      createInvoice(invoice);

      // Clear onboarding data
      clearOnboardingData();

      // Auto login
      setCurrentUser(user);
      loadUserData(user);

      return { success: true };
    } catch (error) {
      console.error('Payment error:', error);
      return { success: false, error: 'Erro ao processar pagamento' };
    }
  };

  const updateProfile = async (data: Partial<User>): Promise<boolean> => {
    if (!state.user) return false;
    
    const { updateUser } = await import('../services/storage');
    const updated = updateUser(state.user.id, data);
    
    if (updated) {
      setState(prev => ({
        ...prev,
        user: { ...prev.user!, ...data }
      }));
      setCurrentUser({ ...state.user, ...data });
      return true;
    }
    return false;
  };

  const isAdmin = state.user?.role === 'admin';

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      logout,
      register,
      selectPlan,
      completePayment,
      updateProfile,
      isAdmin,
      refreshUserData
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to get invoices count
function getInvoices() {
  const { getInvoices } = require('../services/storage');
  return getInvoices();
}
